// @ts-nocheck
import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppRoutes from './AppRoutes';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import { getMbnTypes } from './redux/actions/general';
import SocketUpdater from './components/Socket/SocketUpdater';
import {
    getMatchedOrFirstAccessibleMenuPath,
    checkCurrentUser,
    redirectToLogin,
    getLastPath,
    removeLastPath,
} from './utils/auth';
import {
    getCompetitionTypes,
    getCities,
    getSports,
    getStatuses,
    getTicketSubStatuses,
    getPaymentMethods,
    getPaymentProviders,
    getSources,
    getOperationTypes,
    getConditionTypes,
    getClientTypes,
    getChannelTypes,
} from './redux/actions/staticData';
import TicketInformation from './components/shared/TicketInformation/TicketInformation';
import ErrorBoundary from './components/shared/ErrorBoundary/ErrorBoundary';
import { cancelDownloadFile, fetchAppVersion, setDownloadFileList } from './redux/actions/global';
import AppVersion from './components/shared/AppVersion/AppVersion';
import SettlementInfoDrawer from './components/shared/SettlementInfoDrawer/SettlementInfoDrawer';
import ConfirmModal from './components/shared/ConfirmModal/ConfirmModal';
import TranslationModeBar from './components/TranslationModeBar/TranslationModeBar';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { setUserFetching } from './redux/actions/auth';
import axios from 'axios';
import ExportAllModal from './components/shared/ExportAllModal/ExportAllModal';
import ROUTES from './utils/routes';
import { generateNewBackOfficeUrl, NEW_BO_APP_REDIRECT_MAP } from './utils/new-bo-urls';

const queryClient = new QueryClient({
    defaultOptions: {
        retry: false,
        refetchOnWindowFocus: false,
    },
    queryCache: new QueryCache({
        onError: error => {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                redirectToLogin();
            } else if (error.message === 'Network Error') {
                notification.error({
                    message: 'Network error. Request timed out.',
                });
            }
        },
    }),
});

const App = () => {
    const dispatch = useDispatch();
    const { user, isFetching: isFetchingUser, tempLoginData } = useSelector(state => state.auth);
    const currentDownloadFileList = useSelector(state => state.global.downloadFileList);
    const isAuth = useMemo(() => Object.keys(user).length > 0, [user]);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        dispatch(fetchAppVersion());

        notification.config({
            placement: 'bottomLeft',
        });
    }, [dispatch]);

    useEffect(() => {
        const checkUser = async () => {
            await checkCurrentUser();
            dispatch(setUserFetching(false));
        };

        checkUser();
    }, []);

    useEffect(() => {
        const newBoAppPath = NEW_BO_APP_REDIRECT_MAP[location.pathname];

        if (newBoAppPath !== undefined && window.location.hostname !== 'localhost') {
            window.location.replace(generateNewBackOfficeUrl(newBoAppPath));
        }
    }, [location.pathname]);

    useEffect(() => {
        if (isFetchingUser) return;

        if (!Object.keys(user).length) {
            if (location.pathname !== ROUTES.LOGIN.path && tempLoginData === null) {
                redirectToLogin();
            }
        } else {
            if (location.pathname === '/query-demo') return;
            const redirectPath = getMatchedOrFirstAccessibleMenuPath(
                getLastPath() ?? location.pathname,
                user.roleNames,
            );

            removeLastPath();

            if (redirectPath !== location.pathname) {
                navigate(redirectPath);
            }
        }
    }, [user, isFetchingUser, location.pathname, tempLoginData]);

    useEffect(() => {
        if (isAuth) {
            Sentry.setUser({ username: user.username });
        }
    }, [isAuth, user]);

    useEffect(() => {
        if (isAuth) {
            dispatch(getSports());
            dispatch(getMbnTypes());
            dispatch(getStatuses());
            dispatch(getCities());
            dispatch(getCompetitionTypes());
            dispatch(getTicketSubStatuses());
            dispatch(getClientTypes());
            dispatch(getPaymentMethods());
            dispatch(getPaymentProviders());
            dispatch(getSources());
            dispatch(getOperationTypes());
            dispatch(getConditionTypes());
            dispatch(getChannelTypes());
        }
    }, [dispatch, isAuth]);

    useEffect(() => {
        // dispatch(fetchAppTranslations());
    }, [dispatch]);

    // useEffect(() => {
    //     if (window.innerWidth >= BREAKPOINTS.sm) {
    //         jiraHelpdesk(function () {
    //             var DOMContentLoaded_event = document.createEvent('Event');
    //             DOMContentLoaded_event.initEvent('DOMContentLoaded', true, true);
    //             window.document.dispatchEvent(DOMContentLoaded_event);
    //         });
    //     }
    // }, []);
    // const jiraHelpdesk = callback => {
    //     var jhdScript = document.createElement('script');
    //     jhdScript.type = 'text/javascript';
    //     jhdScript.setAttribute('data-jsd-embedded', null);
    //     jhdScript.setAttribute('data-key', '564751d0-02da-4034-93e3-15c108b16c08');
    //     jhdScript.setAttribute('data-base-url', 'https://jsd-widget.atlassian.com');
    //     jhdScript.src = 'https://jsd-widget.atlassian.com/assets/embed.js';
    //
    //     jhdScript.onload = function () {
    //         callback();
    //     };
    //
    //     document.getElementsByTagName('head')[0].appendChild(jhdScript);
    // };

    return (
        <QueryClientProvider client={queryClient}>
            <ErrorBoundary>
                <TranslationModeBar />
                <SocketUpdater />
                <AppRoutes />
                <TicketInformation />
                <SettlementInfoDrawer />
                <AppVersion />
                <ConfirmModal />

                <ExportAllModal
                    downloadFileList={currentDownloadFileList}
                    onClose={() => {
                        currentDownloadFileList.forEach(item => {
                            item.cancel()?.();
                        });
                        dispatch(setDownloadFileList([]));
                    }}
                    onExportCancel={fileId => {
                        const cancelledFile = currentDownloadFileList.find(item => item.id === fileId);
                        if (cancelledFile?.cancel) {
                            cancelledFile.cancel();
                        }

                        dispatch(cancelDownloadFile(fileId));
                    }}
                />
            </ErrorBoundary>
            <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
    );
};

export default App;
